<script lang="ts">
  import { onMount } from 'svelte';
  import { Editor } from '@tiptap/core';
  import CodeIcon from '$icons/CodeIcon.svelte';
  import BlockQuoteIcon from '$icons/formatting/BlockQuoteIcon.svelte';
  import BoldIcon from '$icons/formatting/BoldIcon.svelte';
  import ItalicIcon from '$icons/formatting/ItalicIcon.svelte';
  import UnderlineIcon from '$icons/formatting/UnderlineIcon.svelte';
  import Button from '$components/reusable/Button.svelte';
  import { ImageUploader } from '$utils/image';
  import LoaderIcon from '$icons/loaders/LoaderIcon.svelte';
  import ErrorWarningAlertIcon from '$icons/ErrorWarningAlertIcon.svelte';
  import ImageIcon from '$icons/files/ImageIcon.svelte';
  import HotkeyCheatsheet from './HotkeyCheatsheet.svelte';

  export let editor: Editor;
  export let errors: string[] = [];
  export let element: HTMLElement | undefined = undefined;
  export let showHotkeyCheatsheet = false;

  const imageUploader = new ImageUploader();

  let isUploadingImage = false;
  const editorStates = {
    bold: false,
    italic: false,
    underline: false,
    blockquote: false,
    codeBlock: false,
    code: false,
  };

  async function handleUploadImage(e: Event) {
    isUploadingImage = true;
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) {
      const response = await imageUploader.compressAndUpload(file);
      editor?.commands.setImage({ src: response.url });
    }
    isUploadingImage = false;
  }

  async function handlePaste(e: ClipboardEvent) {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageItem = Array.from(items).find((item) => item.type.startsWith('image/'));
    if (!imageItem) return;

    e.preventDefault();
    isUploadingImage = true;

    try {
      const file = imageItem.getAsFile();
      if (file) {
        const response = await imageUploader.compressAndUpload(file);
        editor?.chain().focus().deleteSelection().setImage({ src: response.url }).run();
      }
    } catch (error) {
      console.error('Failed to upload pasted image:', error);
    } finally {
      isUploadingImage = false;
    }
  }

  function embolden() {
    editor.chain().focus().toggleBold().run();
  }

  function italicize() {
    editor.chain().focus().toggleItalic().run();
  }

  function underline() {
    editor.chain().focus().toggleUnderline().run();
  }

  function blockQuote() {
    editor.chain().focus().toggleBlockquote().run();
  }

  function toggleCode() {
    const { from, to } = editor.state.selection;

    // No selection - toggle code block
    if (from === to) {
      editor.chain().focus().toggleCodeBlock().run();
      return;
    }

    // If it's already a code block, toggle it off
    if (editor.isActive('codeBlock')) {
      editor.chain().focus().toggleCodeBlock().run();
      return;
    }

    // If it's already inline code, toggle it off
    if (editor.isActive('code')) {
      editor.chain().focus().toggleCode().run();
      return;
    }

    // Check for multiple blocks
    let blockCount = 0;
    editor.state.doc.nodesBetween(from, to, (node) => {
      if (node.type.name === 'paragraph' || node.type.name === 'heading') {
        blockCount++;
        if (blockCount > 1) return false; // Stop traversal
      }
    });

    // Multiple blocks - wrap in code block
    if (blockCount > 1) {
      editor
        .chain()
        .focus()
        .deleteRange({ from, to })
        .insertContent({
          type: 'codeBlock',
          content: [
            {
              type: 'text',
              text: editor.state.doc.textBetween(from, to, '\n'),
            },
          ],
        })
        .run();
    } else {
      // Single line - use inline code
      editor.chain().focus().toggleCode().run();
    }
  }

  onMount(() => {
    const pasteHandler = ((e: Event) => handlePaste(e as ClipboardEvent)) as EventListener;
    editor.options.element.addEventListener('paste', pasteHandler);

    // Subscribe to editor state changes
    editor.on('transaction', () => {
      editorStates.bold = editor.isActive('bold');
      editorStates.italic = editor.isActive('italic');
      editorStates.underline = editor.isActive('underline');
      editorStates.blockquote = editor.isActive('blockquote');
      editorStates.codeBlock = editor.isActive('codeBlock');
      editorStates.code = editor.isActive('code');
    });

    return () => {
      editor.options.element.removeEventListener('paste', pasteHandler);
    };
  });

  $: {
    if (element) {
      editor.options.element.className = 'absolute inset-0 overflow-y-auto';
      (editor.options.element as HTMLElement).style.maxHeight = '100%';
      element.replaceWith(editor.options.element);
    }
  }
</script>

<div class="flex h-full flex-col">
  <div class="border-gray-dark-4 flex min-h-0 flex-1 flex-col rounded border">
    <div class="border-gray-dark-4 bg-gray-dark-2 flex shrink-0 items-center gap-x-2 border-b p-2">
      <!-- Bold -->
      <Button
        tabindex={-1}
        secondary={editorStates.bold}
        tertiary={!editorStates.bold}
        icon
        class="relative"
        on:click={embolden}><BoldIcon class="h-5 w-5" /></Button
      >
      <!-- Italics -->
      <Button
        tabindex={-1}
        secondary={editorStates.italic}
        tertiary={!editorStates.italic}
        icon
        class="relative"
        on:click={italicize}><ItalicIcon class="h-5 w-5" /></Button
      >
      <!-- Underline -->
      <Button
        tabindex={-1}
        secondary={editorStates.underline}
        tertiary={!editorStates.underline}
        icon
        class="relative"
        on:click={underline}><UnderlineIcon class="h-5 w-5" /></Button
      >
      <!-- Block Quote -->
      <Button
        tabindex={-1}
        secondary={editorStates.blockquote}
        tertiary={!editorStates.blockquote}
        icon
        class="relative"
        on:click={blockQuote}><BlockQuoteIcon class="h-5 w-5" /></Button
      >
      <!-- Code block -->
      <Button
        tabindex={-1}
        secondary={editorStates.codeBlock || editorStates.code}
        tertiary={!editorStates.codeBlock && !editorStates.code}
        icon
        class="relative"
        on:click={toggleCode}><CodeIcon class="h-5 w-5" /></Button
      >
      <div class="relative">
        <Button
          tabindex={-1}
          tertiary
          icon
          on:click={() => document.getElementById('image-upload')?.click()}
        >
          {#if isUploadingImage}
            <LoaderIcon class="h-5 w-5" />
          {:else}
            <ImageIcon class="h-5 w-5" />
          {/if}
        </Button>
        <input
          id="image-upload"
          type="file"
          accept="image/*"
          class="hidden"
          on:change={handleUploadImage}
        />
      </div>
      <div class="bg-gray-dark-4 mx-1 hidden h-5 w-px sm:block" />
      <HotkeyCheatsheet bind:open={showHotkeyCheatsheet} />
    </div>
    <!-- Editor -->
    <div class="relative min-h-0 flex-1 overflow-hidden">
      <div bind:this={element} class="absolute inset-0 overflow-hidden" />
    </div>
  </div>
  {#if errors?.length > 0}
    {#each errors as error}
      {#if error}
        <div class="text-red-dark-10 mt-2 flex items-center justify-start gap-x-1 gap-y-0.5">
          <ErrorWarningAlertIcon />
          <p class="text-red-dark-10">{error}</p>
        </div>
      {/if}
    {/each}
  {/if}
</div>
